/* eslint-disable max-len */
import { h, Fragment } from 'preact';
import { useMemo, useRef, useEffect, useState } from 'preact/hooks';
import clsx from 'clsx';
import * as queries from 'utils/storefront/queries';
import Panel from '../utils/panel';
import {
  formatProduct,
  formatShoppingGives,
  formatTryBeforeYouBuy,
} from '../utils/storefront/format-resource';
import Price from './product-price';
import SwatchItem from './product-swatch-item';
import AddToCartButton from './product-add-to-cart';
import { formatMetaobjectList, waitForElm, debounce } from '../utils/helpers';
import Image from '../utils/image';
import AddToCartSticky from './product-sticky-add-to-cart';
import GiftWrapMessage from './product-gift-wrap-message';
// eslint-disable-next-line import/no-named-as-default, import/no-named-as-default-member
import Accordions from './product-accordions';
import SubscriptionCallout from './product-subscription-callout'
import { useWindowSize } from '../utils/window-size';
import BackInStock from './product-bis-form';
import Rise from './product-rise-form';
import UpsellProduct from './product-upsell';
import ScentColors from './product-scent-colors';
import YotpoStars from './product-stars';
import KitOptionGroup from './kit-option-group';
import CloseBig from '../utils/icon/close-big.svg';
import Storefront from '../utils/storefront';
import RichText from '../utils/richtext';

// eslint-disable-next-line max-len
const ProductForm = ({
  currentProduct,
  currentVariant,
  setCurrentProduct,
  setCurrentVariant,
  customClass,
  allProducts,
}) => {
  const scentColors = formatMetaobjectList(
    currentProduct?.scentColors?.references?.edges,
  );
  const sizeOrder = JSON.parse(
    document.querySelector('[data-size-order]').textContent,
  )
    .sizeOrder.split(',')
    .map((s) => s.toLowerCase());
  const descriptionIconsList = formatMetaobjectList(
    currentProduct?.descriptionIcons?.references?.edges,
  );
  // eslint-disable-next-line no-extra-boolean-cast
  const sizeTypeText = !!currentProduct?.burnedOption
    ? window.TMW.Strings.product.time
    : window.TMW.Strings.product.weight;
  // eslint-disable-next-line no-extra-boolean-cast
  const isSingleVariant = allProducts?.data?.products?.nodes?.length === 1;
  const promoMessage = currentProduct?.promoMessage
    ? currentProduct.promoMessage.value
    : allProducts.globalPromoMessage;
  const customPromotions = currentProduct?.excludePromo?.value;
  const compareAtPriceBadge = currentProduct?.compareAtPriceBadge?.value;
  const showGiftWrappingMessage = allProducts?.giftWrappingMessage && !currentProduct.tags.includes('oversized') && (currentProduct?.isGiftCard?.value !== 'true')
  const atcRef = useRef(null);
  const { isTablet, isMobile } = useWindowSize();
  const closeBis = new CustomEvent('bis:close');
  const closeRise = new CustomEvent('rise:close');
  const openTry = new CustomEvent('try:open');
  const closeTry = new CustomEvent('try:close');
  const [isSubscribe, setIsSubscribe] = useState(false);
  const [subscribeId, setSubscribeId] = useState(false);
  const [defaultSubscribeId, setDefaultSubscribeId] = useState(null);
  const [defaultSubscribePrice, setDefaultSubscribePrice] = useState(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [reviewsReRender, setReviewsReRender] = useState(false);
  const [rechargeLoaded, setRechargeLoaded] = useState(false);
  const [shoppingGivesContent, setShoppingGivesContent] = useState(null);
  const [showPromoMessage, setShowPromoMessage] = useState(false)
  const [tryBeforeYouBuyContent, setTryBeforeYouBuyContent] = useState(currentProduct.tryBeforeYouBuy?.reference && formatTryBeforeYouBuy(currentProduct.tryBeforeYouBuy.reference));
  const storefront = new Storefront();

  const handlePagerScroll = (reivewsPosition) => {
    window.scrollTo({
      behavior: 'smooth',
      top: reivewsPosition,
    });
    setReviewsReRender(!reviewsReRender);
  };

  const getShoppingGives = async () => {
    try {
      const response = await storefront.request({
        query: queries.shoppingGives,
        variables: {
          countryCode: window.TMW.localization.countryCode,
          languageCode: window.TMW.localization.languageCode,
        },
      });

      const formattedShoppingGives = formatShoppingGives(response);
      setShoppingGivesContent(formattedShoppingGives[0]);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  const waitRecharge = () => {
    const waiter = setInterval(() => {
      if (window.ReChargeWidget !== undefined) {
        clearInterval(waiter);
        setRechargeLoaded(true);
      }
    }, 10);
  };

  useEffect(() => {
    waitRecharge();
    getShoppingGives();
  }, []);

  useEffect(() => {
    const formattedTryBeforeYouBuy = formatTryBeforeYouBuy(currentProduct.tryBeforeYouBuy?.reference);
    setTryBeforeYouBuyContent(formattedTryBeforeYouBuy);
  }, [currentProduct])

  useEffect(() => {
    waitForElm('nav .yotpo-pager:not(.event-added)').then(() => {
      const reivewsPosition =
        document.querySelector('.yotpo-main-widget').offsetTop - 1000;
      document.querySelector('nav .yotpo-pager').classList.add('event-added');
      document
        .querySelectorAll('nav .yotpo-pager .yotpo-page-element.goTo')
        .forEach((pager) => {
          pager.addEventListener('click', () =>
            handlePagerScroll(reivewsPosition),
          );
        });
    });
  }, [reviewsReRender]);
  const [isKits, setIsKits] = useState(false);

  useEffect(() => {
    const tryItTriggers = document.querySelectorAll('a[href="#tryit"]');
    tryItTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        document.dispatchEvent(openTry);
      });
    });
  }, [tryBeforeYouBuyContent]);

  useEffect(() => {
    // Ensure the url changes when the "variant" changes
    const localization =
      window.TMW.localization.localeString === 'en-us'
        ? ''
        : `/${window.TMW.localization.localeString}`;
    if (currentProduct) {
      const url = `${localization}/products/${currentProduct?.handle}/`;
      window.history.replaceState({}, '', url);
    }

    if (
      rechargeLoaded &&
      window.TMW?.localization?.currencyCode === 'USD' &&
      currentProduct
    ) {
      const reChargeWidgets = window.ReChargeWidget.getWidgets();
      if (reChargeWidgets.length > 0) {
        setTimeout(() => {
          window.ReChargeWidget?.destroyAllWidgets();
          window.ReChargeWidget?.createWidget({
            productId: parseInt(
              currentProduct?.id.replace('gid://shopify/Product/', ''),
              10,
            ),
          });
        }, 500);
        setIsSubscribe(false);
        setSubscribeId(false);
      } else {
        setTimeout(() => {
          if (reChargeWidgets.length === 0) {
            window.ReChargeWidget?.createWidget({
              productId: parseInt(
                currentProduct?.id.replace('gid://shopify/Product/', ''),
                10,
              ),
            });
          }
        }, 1000);
      }
    }
  }, [currentProduct, rechargeLoaded]);

  useEffect(() => {
    if ((!currentProduct.shoppingGives || currentProduct.shoppingGives.value === 'false') && (!currentProduct.tryBeforeYouBuy || !currentProduct.tryBeforeYouBuy.reference)) {
      setShowPromoMessage(true)
      return
    }
    if ((currentProduct.shoppingGives && shoppingGivesContent) || (currentProduct.tryBeforeYouBuy && tryBeforeYouBuyContent)) {
      setShowPromoMessage(false)
    } else {
      setShowPromoMessage(true)
    }
  }, [currentProduct, shoppingGivesContent, tryBeforeYouBuyContent])

  const descriptionIcons = useMemo(() => {
    const kitOptionGroups = [
      {
        products:
          currentProduct?.kitOptionGroup1?.references?.nodes.map(formatProduct),
        label: currentProduct?.kitOptionGroup1Label?.value,
        key: 'kitOptionGroup1',
      },
    ];
    // eslint-disable-next-line no-unused-expressions
    !kitOptionGroups[0].products ? setIsKits(false) : setIsKits(true);

    return (
      <div className="px-5 laptop:px-0">
        <div
          className={clsx(
            isKits && 'pt-5 laptop:pt-8 border-y border-black-30 pb-4',
            'my-8',
          )}
        >
          <div
            className="font-14 text-black rte"
            // eslint-disable-next-line max-len
            dangerouslySetInnerHTML={{
              __html: currentProduct?.descriptionHtml,
            }}
          ></div>
          <div
            className={clsx(
              !!descriptionIconsList.length && 'my-4',
              ' flex flex-wrap gap-5',
            )}
          >
            {!!descriptionIconsList.length &&
              descriptionIconsList.map((icon) => (
                <div
                  key={icon.handle}
                  className="flex max-w-[calc(50%-10px)] gap-[13px] items-center w-full"
                >
                  <div className="w-[30px] h-[30px] min-w-[30px]">
                    <Image
                      classes="w-full"
                      src={icon.icon}
                      srcWidths={{
                        sm: 640,
                        md: 768,
                        lg: 1024,
                        xl: 1280,
                        '2xl': 1536,
                      }}
                    />
                  </div>
                  <p className="utility-1 text-black">{icon.text}</p>
                </div>
              ))}
          </div>
        </div>
      </div>
    );
  }, [currentProduct]);

  const blocks = useMemo(() => (
    <>
      {allProducts.blocks.map((block) => (
        <>
          {block?.type === "nosto_placement" && !!block?.id && (<div
            className="nosto_element"
            id={block.id}
            key={block.id}
          ></div>
          )}
        </>
      ))}
    </>
  ));

  const productSwatches = useMemo(
    () => (
      <>
        {allProducts?.data?.productByHandle &&
          allProducts?.data?.productByHandle?.variants?.edges.length > 1 && (
            <div className="px-5 laptop:px-0">
              <label className="block font-13-bold mb-2">
                {allProducts?.data?.productByHandle?.options[0]?.name ||
                  'Select one'}
              </label>
              <select
                className="select w-full mb-5"
                value={currentVariant.id}
                onChange={(e) => {
                  setCurrentVariant(
                    allProducts?.data?.productByHandle?.variants?.edges.find(
                      (variant) => variant.node.id === e.target.value,
                    ).node,
                  );
                }}
              >
                {allProducts?.data?.productByHandle?.variants?.edges.map(
                  (variantOption) => (
                    <option
                      key={variantOption.node.id}
                      value={variantOption.node.id}
                      disabled={!variantOption.node.availableForSale}
                    >
                      {variantOption.node.title}
                      {!variantOption.node.availableForSale &&
                        ' - out of stock'}
                    </option>
                  ),
                )}
              </select>
            </div>
          )}
        {allProducts?.data?.products && (
          <div className="mb-8 px-5 laptop:px-0">
            <p className="text-black font-13-bold mb-2">
              {window.TMW.Strings.product.size} | {sizeTypeText}:{' '}
              {!currentProduct?.availableForSale && (
                <span className="font-13-bold text-grey-60">
                  {window.TMW.Strings.product.outOfStock}
                </span>
              )}
            </p>
            <div
              className={clsx(
                'flex flex-wrap gap-2.5 laptop:justify-start',
                allProducts?.data?.products?.nodes.length > 2
                  ? 'justify-between'
                  : 'justify-start',
              )}
            >
              {allProducts?.data?.products?.nodes?.map((product) => (
                <SwatchItem
                  key={product.id}
                  setCurrentProduct={setCurrentProduct}
                  setCurrentVariant={setCurrentVariant}
                  currentProduct={product}
                  isActive={product.id === currentProduct?.id}
                  isSingleVariant={isSingleVariant}
                  sizeOrder={sizeOrder}
                />
              ))}
            </div>
          </div>
        )}
      </>
    ),
    [currentProduct, currentVariant],
  );

  const [selectedKitItems, setSelectedKitItems] = useState([]);

  const updateKitSelection = (index, selectedProduct) => {
    const newKitItems = selectedKitItems;
    newKitItems[index] = selectedProduct;
    setSelectedKitItems(newKitItems);
  };

  const kitSelectors = useMemo(() => {
    const kitOptionGroups = [
      {
        products:
          currentProduct?.kitOptionGroup1?.references?.nodes.map(formatProduct),
        label: currentProduct?.kitOptionGroup1Label?.value,
        key: 'kitOptionGroup1',
      },
      {
        products:
          currentProduct?.kitOptionGroup2?.references?.nodes.map(formatProduct),
        label: currentProduct?.kitOptionGroup2Label?.value,
        key: 'kitOptionGroup2',
      },
      {
        products:
          currentProduct?.kitOptionGroup3?.references?.nodes.map(formatProduct),
        label: currentProduct?.kitOptionGroup3Label?.value,
        key: 'kitOptionGroup3',
      },
      {
        products:
          currentProduct?.kitOptionGroup4?.references?.nodes.map(formatProduct),
        label: currentProduct?.kitOptionGroup4Label?.value,
        key: 'kitOptionGroup4',
      },
      {
        products:
          currentProduct?.kitOptionGroup5?.references?.nodes.map(formatProduct),
        label: currentProduct?.kitOptionGroup5Label?.value,
        key: 'kitOptionGroup5',
      },
    ];

    // dont bother rendering kit selections if first product list is empty
    if (!kitOptionGroups[0].products) return null;

    // set default kit options to state
    kitOptionGroups.forEach((group, index) => {
      if (!group.products) return;
      updateKitSelection(index, group.products[0]);
    });

    return (
      <div className="mt-8 mb-3 px-[20px] laptop:px-0">
        {kitOptionGroups.map(
          (kitGroup, index) =>
            kitGroup.products && (
              <KitOptionGroup
                key={kitGroup.key}
                label={kitGroup.label}
                products={kitGroup.products}
                updateKitSelection={(selectedProduct) =>
                  updateKitSelection(index, selectedProduct)
                }
              />
            ),
        )}
      </div>
    );
  }, [currentProduct]);

  const price = useMemo(
    () => (
      <Price
        currentPrice={currentVariant?.price}
        currentComparePrice={currentVariant?.compareAtPrice}
        fontClass="font-x-large text-black-2"
        customContainerClass="mb-8 px-5 laptop:px-0"
        customPromotions={customPromotions}
        compareAtPriceBadge={compareAtPriceBadge}
        promoFlag={currentProduct?.promoFlag?.value}
      />
    ),
    [currentProduct, currentVariant],
  );

  const addToCartButton = useMemo(
    () => (
      <AddToCartButton
        currentVariant={currentVariant}
        currentProduct={currentProduct}
        showQty={true}
        promoMessage={promoMessage}
        selectedKitItems={selectedKitItems}
        subscribeId={subscribeId}
        showMessage={showPromoMessage}
        showGiftSend={currentProduct?.isGiftCard?.value}
      />
    ),
    [
      currentVariant,
      currentProduct,
      promoMessage,
      subscribeId,
      shoppingGivesContent,
      tryBeforeYouBuyContent,
    ],
  );

  const giftWrapMessage = useMemo(() => (<GiftWrapMessage data={allProducts?.giftWrappingMessage} />))

  const accordions = useMemo(
    () => (
      <Accordions
        currentProduct={currentProduct}
        currentVariant={currentVariant}
        tryBeforeYouBuy={tryBeforeYouBuyContent?.explainerText}
        isPdp={true}
      />
    ),
    [currentProduct, tryBeforeYouBuyContent],
  );

  const bisPanel = useMemo(
    () => (
      <Panel
        defaultOpen={false}
        context="bis"
        additionalClasses={'px-5 laptop:px-[60px]'}
        showBackdrop={true}
      >
        <button
          type="button"
          onClick={() => document.dispatchEvent(closeBis)}
          className="absolute top-6 right-5"
        >
          <CloseBig />
        </button>
        <BackInStock currentVariant={currentVariant} />
      </Panel>
    ),
    [currentVariant],
  );

  const risePanel = useMemo(
    () => (
      <Panel
        defaultOpen={false}
        context="rise"
        additionalClasses={
          'px-5 laptop:px-[60px] !max-w-panelxl overflow-y-scroll tablet:overflow-y-hidden flex items-start tablet:items-center justify-center'
        }
        showBackdrop={true}
      >
        <button
          type="button"
          onClick={() => document.dispatchEvent(closeRise)}
          className="absolute top-6 right-5"
        >
          <CloseBig />
        </button>
        <Rise currentVariant={currentVariant} currentProduct={currentProduct} />
      </Panel>
    ),
    [currentVariant],
  );

  const tryPanel = useMemo(
    () => (
      <Panel
        defaultOpen={false}
        context="try"
        additionalClasses={
          'px-5 laptop:px-[60px] overflow-y-scroll !bg-neutral'
        }
        showBackdrop={true}
      >
        <button
          type="button"
          onClick={() => document.dispatchEvent(closeTry)}
          className="absolute top-6 right-5"
        >
          <CloseBig />
        </button>
        <div className="flex flex-col gap-8 pt-20 pb-10">
          {tryBeforeYouBuyContent && (
            <>
              <Image
                classes="w-full"
                src={tryBeforeYouBuyContent?.detailsImage?.image?.originalSrc}
                srcWidths={{
                  sm: 460,
                }}
              />
              <div className="flex flex-col gap-4">
                <p className="subheading-h2">
                  {tryBeforeYouBuyContent?.detailsHeading}
                </p>
                {tryBeforeYouBuyContent?.detailsContent && <RichText
                  data={JSON.parse(tryBeforeYouBuyContent?.detailsContent)}
                  customRootClass="!mb-0"
                />}
              </div>
            </>
          )}
        </div>
      </Panel>
    ),
    [tryBeforeYouBuyContent],
  );

  const upsellProduct = useMemo(
    () => (
      <UpsellProduct
        currentVariant={currentProduct?.upsellProduct?.reference}
      />
    ),
    [currentProduct],
  );

  const yotpoStars = useMemo(
    () => (
      <YotpoStars
        currentProduct={currentProduct}
        currentVariant={currentVariant}
        isProductPage={true}
      />
    ),
    [currentProduct, currentVariant],
  );

  const productForm = useMemo(() => {
    if (rechargeLoaded && window.TMW.localization.currencyCode === 'USD') {
      return (
        <form
          method="post"
          action="/cart/add"
          acceptCharset="UTF-8"
          encType="multipart/form-data"
          data-type="add-to-cart-form"
          noValidate
          data-product-form
          data-product-id={currentProduct?.id}
        />
      );
    }
    return null;
  }, [currentProduct, currentVariant, rechargeLoaded]);

  const shoppingGives = useMemo(
    () => (
      <div className="px-5 laptop:px-0">
        {(currentProduct?.tryBeforeYouBuy?.reference ||
          currentProduct.shoppingGives?.value === 'true') && (
          <div className="py-8 border-b border-black-30">
            <div className="gap-3 flex flex-col justify-start px-2.5 py-3 border-black-30 rounded border">
              {currentProduct?.tryBeforeYouBuy?.reference &&
                tryBeforeYouBuyContent && (
                  <div className="flex flex-col gap-1" key="try-before-you-bu">
                    <div className="flex gap-2.5 items-center">
                      <Image
                        classes="w-full max-w-[56px]"
                        src={tryBeforeYouBuyContent?.icon?.image?.originalSrc}
                        srcWidths={{
                          sm: 224,
                        }}
                        alt={tryBeforeYouBuyContent?.icon?.image?.altText}
                      />
                      <div>
                        {tryBeforeYouBuyContent?.content && <RichText
                          data={JSON.parse(tryBeforeYouBuyContent?.content)}
                          customRootClass={'!mb-0'}
                        />}
                      </div>
                    </div>
                  </div>
                )}
              {currentProduct?.tryBeforeYouBuy?.reference &&
                currentProduct.shoppingGives?.value === 'true' && (
                  <div className="w-full h-[1px] bg-black-30" />
                )}
              {currentProduct.shoppingGives?.value === 'true' &&
                shoppingGivesContent && (
                  <div className="flex flex-col gap-1" key="shipping-gives">
                    <div className="flex gap-2.5 items-center">
                      <Image
                        classes="w-full max-w-[56px]"
                        src={shoppingGivesContent?.icon?.image?.originalSrc}
                        srcWidths={{
                          sm: 224,
                        }}
                        alt={shoppingGivesContent?.icon?.image?.altText}
                      />
                      <div>
                        <RichText
                          data={JSON.parse(shoppingGivesContent?.content)}
                          customRootClass={'!mb-0'}
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-start gap-1">
                      <span className="utility-3 text-black">Powered by</span>
                      <Image
                        classes="w-full max-w-[84px]"
                        src={
                          shoppingGivesContent?.powered_by_logo?.image
                            ?.originalSrc
                        }
                        srcWidths={{
                          sm: 336,
                        }}
                        alt={shoppingGivesContent?.icon?.image?.altText}
                      />
                    </div>
                  </div>
                )}
            </div>
          </div>
        )}
      </div>
    ),
    [currentProduct, shoppingGivesContent, tryBeforeYouBuyContent],
  );

  const addToCartSticky = (showSubscribeBtn) => useMemo(
    () => (
      <AddToCartSticky
        currentVariant={currentVariant}
        currentProduct={currentProduct}
        atcRef={atcRef}
      >
        <div className='flex gap-x-4 items-center'>
          {showSubscribeBtn &&
            <AddToCartButton
              currentVariant={currentVariant}
              currentProduct={currentProduct}
              showQty={false}
              showPrice={Boolean(defaultSubscribePrice)}
              isMargin={false}
              showMessage={false}
              customClass='hidden laptop:block'
              customButtonClass='btn-primary-stroke gap-x-5'
              subscribeId={defaultSubscribeId}
              showGiftSend={false}
              customPrice={defaultSubscribePrice}
            />
          }
          <AddToCartButton
            currentVariant={currentVariant}
            currentProduct={currentProduct}
            showQty={false}
            showPrice={true}
            isMargin={false}
            selectedKitItems={selectedKitItems}
            showMessage={false}
            customClass={'min-w-[275px]'}
            showGiftSend={currentProduct?.isGiftCard?.value}
          />
        </div>
      </AddToCartSticky>
    ),
    [atcRef, currentVariant, isTablet, isMobile, subscribeId, showSubscribeBtn],
  );

  const subscriptionCallout = useMemo(
    () => <SubscriptionCallout
        data={allProducts.subscriptionCalloutData}
        isLimited={currentProduct?.limitedTimeSubscribeSave?.value === 'true'}
      />,
    [allProducts.subscriptionCalloutData, currentProduct],
  );

  const activateSubscriptionProduct = () => {
    if (isSubscribe) return false;
    setIsSubscribe(true);
    setSubscribeId(
      document.querySelector('.rc_widget__option__plans__dropdown').value,
    );
    return '';
  };

  const deactivateSubscriptionProduct = () => {
    if (!isSubscribe) return false;
    setIsSubscribe(false);
    setSubscribeId(false);
    return '';
  };

  useEffect(() => {
    setHasSubscription(false);

    const checkSubscription = () => {
      waitForElm('.rc_widget__option__plans__dropdown').then(() => {
        const subSave = document.querySelector('[data-option-subsave]');
        const dropdown = document.querySelector(
          '.rc_widget__option__plans__dropdown',
        );
        const oneTime = document.querySelector('[data-option-onetime]');
        setHasSubscription(true)

        setDefaultSubscribeId(dropdown.value);

        const subSavePriceEl = subSave?.querySelector('.rc_widget__price--subsave');
        const subscribePrice = subSavePriceEl?.textContent?.match(/\d+(\.\d+)?/)?.[0];
        setDefaultSubscribePrice(subscribePrice)

        subSave?.addEventListener('click', activateSubscriptionProduct);
        dropdown?.addEventListener('change', activateSubscriptionProduct);
        oneTime?.addEventListener('click', deactivateSubscriptionProduct);
        if (!oneTime) activateSubscriptionProduct();
      });
    };

    const debouncedCheckSubscription = debounce(checkSubscription, 500);

    debouncedCheckSubscription();

    return () => debouncedCheckSubscription.cancel && debouncedCheckSubscription.cancel();
  }, [isSubscribe, currentVariant]);

  return (
    <div className={clsx(customClass, 'product-form')}>
      <div className="desktop:min-w-[437px]">
        <div className="flex items-center justify-start gap-[8.8px] mb-[9px] laptop:mb-[5px] px-[20px] laptop:px-0">
          {yotpoStars}
        </div>
        <div className="mb-[7px] laptop:mb-2.5 px-5 laptop:px-0">
          <h1 className="text-black subheading-h2">{currentProduct?.title}</h1>
        </div>
        <ScentColors scentColors={scentColors} />
        {price}
        {isKits && descriptionIcons}
        {productSwatches}
        {kitSelectors}
        {productForm}
        {hasSubscription && allProducts.subscriptionCalloutData && subscriptionCallout}
        <div ref={atcRef} className="px-5 laptop:px-0 pt-1">
          {addToCartButton}
        </div>
        {showGiftWrappingMessage && giftWrapMessage}
        {addToCartSticky(hasSubscription)}
        {shoppingGives}
        {isKits || descriptionIcons}
        {accordions}
        {bisPanel}
        {risePanel}
        {tryPanel}
        {upsellProduct}
        {blocks}
      </div>
    </div>
  );
};;
export default ProductForm;
